import React from 'react';
import { Alert, Box, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import SupplierEditor from '../components/suppliers/SupplierEditor';
import SupplierUpload from '../components/suppliers/SupplierUpload';
import SupplierLog from '../components/suppliers/SupplierLog';
import supplierApi, { supplierError } from '../services/supplierApi';
import type { CreatedSupplier, SupplierConfig } from '../types/suppliers';

/**
 * Petty Cash > Suppliers: create SAP vendors for the selected company, one by one or from an Excel file.
 * A VAT number SAP already holds is refused and the vendor that has it is named (backend
 * services/supplier.service.ts). Module 'suppliers'.
 */

type TabKey = 'define' | 'upload' | 'log';

const Suppliers: React.FC = () => {
  const companyId = React.useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0, []);
  const [tab, setTab] = React.useState<TabKey>('define');
  const [config, setConfig] = React.useState<SupplierConfig | null>(null);
  const [error, setError] = React.useState('');
  const [created, setCreated] = React.useState<CreatedSupplier | null>(null);
  const [logKey, setLogKey] = React.useState(0);

  React.useEffect(() => {
    if (!companyId) { setError('Select a company first.'); return undefined; }
    let cancelled = false;
    supplierApi.config(companyId)
      .then((c) => { if (!cancelled) setConfig(c); })
      .catch((e) => { if (!cancelled) setError(supplierError(e, 'The supplier lists could not be read from SAP')); });
    return () => { cancelled = true; };
  }, [companyId]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">Petty Cash / Suppliers</Typography>
        <Typography component="h1" variant="h5" fontWeight={600}>Suppliers</Typography>
        {config ? (
          <Typography variant="body2" color="text.secondary">
            New vendors in {config.company.name} ({config.company.databaseName})
            {config.company.vatRegistered ? `. A VAT number is required: ${config.company.vatRule || 'digits only'}.` : '.'}
          </Typography>
        ) : null}
      </Box>

      <Tabs value={tab} onChange={(_e, v: TabKey) => setTab(v)} aria-label="Supplier tasks" variant="scrollable" allowScrollButtonsMobile>
        <Tab value="define" label="Define a supplier" />
        <Tab value="upload" label="Upload from Excel" />
        <Tab value="log" label="Created through PRPO" />
      </Tabs>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {!config && !error ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2">Reading the supplier lists from SAP…</Typography>
        </Stack>
      ) : null}

      {config && tab === 'define' ? (
        <Stack spacing={2}>
          {created ? (
            <Alert severity="success" onClose={() => setCreated(null)}>
              Created in SAP: {created.cardCode} {created.cardName}
              {created.warnings.length ? `. Note: ${created.warnings.join('; ')}` : ''}
            </Alert>
          ) : null}
          <SupplierEditor
            companyId={companyId}
            config={config}
            source="Form"
            layout="page"
            onCreated={(c) => { setCreated(c); setLogKey((k) => k + 1); }}
          />
        </Stack>
      ) : null}
      {config && tab === 'upload' ? <SupplierUpload companyId={companyId} config={config} onCreated={() => setLogKey((k) => k + 1)} /> : null}
      {companyId && tab === 'log' ? <SupplierLog companyId={companyId} refreshKey={logKey} /> : null}
    </Box>
  );
};

export default Suppliers;
